import { ScheduleVersion } from "@iventis/domain-model/model/scheduleVersion";
/* eslint-disable prefer-destructuring */
import { Schedule } from "@iventis/domain-model/model/schedule";
import { createMachine, assign, DoneInvokeEvent } from "xstate";
import { Node } from "@iventis/domain-model/model/node";
import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { findNode } from "@iventis/tree-browser/tree-operations";
import { parseISO } from "date-fns";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { VenueLocation } from "@iventis/domain-model/model/venueLocation";
import { EventBlockDone } from "@iventis/domain-model/model/eventBlockDone";
import { BehaviorSubject } from "rxjs";
import { scheduleApi } from "@iventis/api/src/api";
import { boolToString, EMPTY_GUID, getDataFieldListItems } from "@iventis/utilities";
import { useHistory } from "@iventis/subscriptions";
import {
    SchedulesWaitingFor,
    ScheduleVersionChildCopied,
    ScheduleMachineContext,
    ScheduleMachineEvent,
    ScheduleVersionNodeCreated,
    UpdateScheduleEdit,
    DeleteLocalScheduleVersion,
} from "./schedule.machine.types";
import { ScheduleVersionNode } from "./schedule.types";
import { getSchedule, getScheduleData, getHierarchyEventBlocks, getScheduleVersionData, deleteScheduleVersion, getEventBlocksDone } from "./schedule-api-functions";
import {
    createDayDateMap,
    createScheduleVersionNode,
    removeEventBlocksWithScheduleVersion,
    findScheduleNode,
    flattenVenueLocations,
    getCalendarRangeByDay,
    includeThirdLevelVenue,
    getCalendarRangeBasedOnFilterType,
} from "./schedule-helper-functions";
import { generateScheduleViewByOptions } from "./view-by-selector";
import { FilterType } from "./filter-rules";
import { findHierarchyEntityById } from "./hierarchy.helpers";
import { getDisciplineDataField, getSportsFilterableDataFields } from "./datafields";
import { showEventBlockOnCalendar } from "./filter-helper-functions";
import {
    getAndValidateInitialCalendarLevel,
    getAndValidateInitialDay,
    getScheduleIdFromUrlPathname,
    ScheduleQueryParam,
    ScheduleUrlData,
    updateUrlPathnameWithNewScheduleId,
    updateUrlSearchParam,
    updateViewByUrlFromMachine,
} from "./schedule-url-helpers";
import { scheduleWebSocketSub } from "./schedule-web-sockets";

/*
    HELPERS
*/

const DEFAULT_SCHEDULE_NODE_PROPERTIES: Pick<ScheduleVersionNode, "visible" | "editName"> = { visible: true, editName: false };

const isTopLevel = (context: ScheduleMachineContext, event: Partial<UpdateScheduleEdit | DeleteLocalScheduleVersion>) => {
    const schedule =
        typeof event.payload === "string" ? findHierarchyEntityById<ScheduleVersion>(context.availableSchedules.children, event.payload).item : (event.payload as ScheduleVersion);
    return schedule.parentId === null;
};

const convertNodeToScheduleNode = (node: Node, options?: { visible?: boolean; editName?: boolean }) => ({
    ...node,
    visible: options?.visible || DEFAULT_SCHEDULE_NODE_PROPERTIES.visible,
    editName: options?.editName || DEFAULT_SCHEDULE_NODE_PROPERTIES.editName,
});

const convertNodesChildNodesToScheduleNodes = (node: Node, options?: { visible?: boolean; editName?: boolean }) =>
    ({
        ...node,
        childNodes: node.childNodes.map((childNode) => convertNodeToScheduleNode(childNode, options)),
    } as ScheduleVersionNode);

const removeEventBlock = ({ eventBlockHierarchy }: ScheduleMachineContext, event: { payload: string }) => {
    const eventBlockToDelete = findHierarchyEntityById<EventBlock>([eventBlockHierarchy], event.payload);
    // Null check because if the parent has already been deleted from the hierarchy, then so has all of its children.
    if (eventBlockToDelete?.item !== null) {
        if (eventBlockToDelete.item.parentId == null) {
            // Top level event
            // eslint-disable-next-line no-param-reassign
            eventBlockHierarchy.children = eventBlockHierarchy.children.filter(({ item }) => item.id !== event.payload);
        } else {
            const parentEventBlock = findHierarchyEntityById([eventBlockHierarchy], eventBlockToDelete.item.parentId);
            parentEventBlock.children = parentEventBlock.children.filter(({ item }) => item.id !== event.payload);
        }
    }
};

const deleteLocalScheduleVersion = (context: ScheduleMachineContext, id: string, history: ReturnType<typeof useHistory>): ScheduleMachineContext => {
    // Remove the node if it was found in the hierarchy
    const deletedNode = findNode<Node>(context.scheduleNode.childNodes, id);
    if (deletedNode) {
        const deletedNodeParent = findNode<Node>([context.scheduleNode], deletedNode.parentId);
        if (deletedNodeParent) {
            deletedNodeParent.childNodes = deletedNodeParent.childNodes.filter((n) => n.id !== id);
        }
    }

    // Remove the schedule version from hierarchy
    const schedule = findHierarchyEntityById(context.availableSchedules.children, id) || findHierarchyEntityById(context.selectedScheduleVersionHierarchy.children, id); // In future we should combine these two hierarchy's
    if (isTopLevel(context, { payload: schedule.item })) {
        context.availableSchedules.children = context.availableSchedules.children.filter((s) => s.item.id !== id);
        context.selectedScheduleVersionRootId = null;
        // Update the schedule version in the url with the next available schedule
        updateUrlPathnameWithNewScheduleId(history, context.availableSchedules.children[0]?.item.id);
    } else {
        const filteredScheduleVersions = context.selectedScheduleVersionHierarchy.children.filter((s) => s.item.id !== id);
        context.selectedScheduleVersionHierarchy.children = filteredScheduleVersions;

        // Remove event blocks inside schedule version
        context.eventBlockHierarchy = removeEventBlocksWithScheduleVersion(context.eventBlockHierarchy, filteredScheduleVersions);

        // Remove from selected schedule versions array
        context.selectedSubScheduleIds = context.selectedSubScheduleIds.filter((_id) => _id !== id);
    }
    return context;
};

const getScheduleInitialContext = (inputSchedule: Schedule, scheduleUrlData: ScheduleUrlData, history: ReturnType<typeof useHistory>): Partial<ScheduleMachineContext> => {
    const schedule = { ...inputSchedule };
    const { query } = scheduleUrlData;
    // Ensure that schedule dates are ready to use as proper date objects
    schedule.startAt = parseISO(schedule.startAt.toString());
    schedule.endAt = parseISO(schedule.endAt.toString());
    schedule.dayZero = parseISO(schedule.dayZero.toString());
    const filterableDataFields = getSportsFilterableDataFields(schedule.config.dataFields);

    const viewByOptions = generateScheduleViewByOptions(filterableDataFields);

    const viewByOptionFromQuery = query.get(ScheduleQueryParam.ViewBy);

    const viewByOptionSelected = viewByOptionFromQuery == null ? viewByOptions[0] : viewByOptions.find((v) => v.filterType === viewByOptionFromQuery);

    const calendarLevel = getAndValidateInitialCalendarLevel(viewByOptionSelected.filterType, schedule, query);

    const availableDays = createDayDateMap(schedule.startAt, schedule.endAt, schedule.dayZero);

    const initialDay = getAndValidateInitialDay(viewByOptionSelected.filterType, availableDays, query);

    const { day } = getCalendarRangeBasedOnFilterType(viewByOptionSelected.filterType, availableDays, schedule.dayStartAt, schedule.dayEndAt, initialDay, schedule.timezone);

    updateUrlSearchParam(history, query);

    return {
        schedule,
        filterableDataFieldValues: filterableDataFields,
        availableViewByOptions: viewByOptions,
        selectedViewByOption: viewByOptionSelected,
        availableDays,
        day: day ?? initialDay,
        calendarLevel,
    };
};
export const scheduleMachine = (history: ReturnType<typeof useHistory>, locationData$: BehaviorSubject<ScheduleUrlData>) =>
    createMachine(
        {
            id: "schedule",
            context: {
                onScheduleCopyCompleted: undefined,
                schedule: undefined,
                scheduleNode: undefined,
                availableSchedules: undefined,
                selectedScheduleVersionRootId: undefined,
                selectedScheduleVersionHierarchy: undefined,
                selectedSubScheduleIds: [],
                pendingSchedules: [],
                venueLocations: [],
                eventBlockHierarchy: undefined,
                selectedViewByOption: undefined,
                availableViewByOptions: [],
                selectedDataFieldListItems: [],
                availableDataFieldListItems: [],
                selectedVenueLocations: [],
                availableDays: undefined,
                day: undefined,
                filterableDataFieldValues: [],
                historicalNodeCreatedEvents: [],
                eventBlocksDone: [],
                isTableView: false,
                calendarLevel: 1,
            },
            schema: {
                context: {} as ScheduleMachineContext,
                events: {} as ScheduleMachineEvent,
                services: {} as {
                    getSchedule: { data: Schedule };
                    getScheduleData: {
                        data: {
                            scheduleNode: Node;
                            venueLocations: Hierarchy<VenueLocation>[];
                            availableSchedules: Hierarchy<ScheduleVersion>;
                        };
                    };
                    getScheduleVersionData: {
                        data: {
                            scheduleVersionNode: Node;
                            scheduleVersionHierarchy: Hierarchy<ScheduleVersion>;
                        };
                    };
                    getEventBlockData: { data: { eventBlockHierarchy: Hierarchy<EventBlock>; eventBlocksDone: EventBlockDone[] } };
                    deleteScheduleVersion: { data: string };
                    getDataFieldListItems: { data: { listItems: DataFieldListItem[]; filterType: FilterType } };
                },
            },
            // eslint-disable-next-line no-undef
            tsTypes: {} as import("./schedule.machine.typegen").Typegen0,
            initial: "scheduleLoading",
            invoke: {
                id: "scheduleRootServices",
                src: "scheduleRootServices",
            },
            on: {
                /*
                    EVENTS FROM WEB SOCKETS
                */
                SCHEDULE_VERSION_CHILD_COPIED: [
                    {
                        // Decide whether it's time to load the event block hierarchy
                        target: "scheduleLoadingEventBlock",
                        cond: "isCopiedScheduleVersionReadyToLoadEventBlocks",
                        actions: ["updatePendingSchedules", "addressCompletedPendingSchedules"],
                    },
                    {
                        actions: ["updatePendingSchedules", "addressCompletedPendingSchedules"],
                    },
                ],
                SCHEDULE_VERSION_NODE_CREATED: [
                    {
                        cond: "isCreatedNodeNotPending",
                        actions: "updateHistoricalNodeEvents",
                    },
                    {
                        // Decide whether it is time to load the event block hierarchy
                        target: "scheduleLoadingEventBlock",
                        cond: "isCreatedScheduleVersionReadyToLoadEventBlocks",
                        actions: ["updatePendingNodes", "addressCompletedPendingSchedules"],
                    },
                    {
                        actions: ["updatePendingNodes", "addressCompletedPendingSchedules"],
                    },
                ],

                /*
                    EVENTS FROM URL UPDATES
                */
                FROM_URL_SELECT_ROOT_SCHEDULE_VERSION: [
                    {
                        cond: "areScheduleVersionCreationsPending",
                        // Since they're not on the back-end yet, we will just directly add them to state
                        actions: "fromUrlSelectRootScheduleVersion",
                    },
                    // When the condition fails, we do not have pending creations. Therefore we can load from the back-end.
                    { target: "scheduleLoadingScheduleVersions" },
                ],
                FROM_URL_UPDATE_VIEW_BY: [
                    {
                        cond: "isViewByDataField",
                        actions: "viewByDataField",
                        target: "loadViewByDataField",
                    },
                    {
                        cond: "isViewByDayDataField",
                        actions: "viewByDayDataField",
                        target: "loadViewByDataField",
                    },
                    {
                        cond: "isViewByDayVenueOrSummary",
                        actions: "viewByDayVenueOrSummary",
                        target: "scheduleLoadingEventBlock",
                    },
                    {
                        cond: "isViewByVenue",
                        actions: "viewByVenue",
                        target: "scheduleLoadingEventBlock",
                    },
                    {
                        cond: "isViewByVenueRunSheet",
                        actions: "viewByVenueRunSheet",
                        target: "scheduleLoadingEventBlock",
                    },
                ],
                FROM_URL_UPDATE_SELECTED_VENUE_LOCATION: {
                    actions: "fromUrlUpdateSelectedVenueLocations",
                    target: "scheduleLoadingEventBlock",
                },
                FROM_URL_UPDATE_SELECTED_DATA_FIELD_LIST_ITEMS: {
                    actions: "fromUrlUpdateSelectedDataFieldListItems",
                    target: "scheduleLoadingEventBlock",
                },
                FROM_URL_UPDATE_CALENDAR_LEVEL: {
                    actions: "fromUrlUpdateCalendarLevel",
                },
                FROM_URL_UPDATE_IS_TABLE_VIEW: {
                    actions: "fromUrlUpdateIsTableView",
                },
                FROM_URL_UPDATE_DAY: {
                    actions: "fromUrlUpdateDay",
                    target: "scheduleLoadingEventBlock",
                },
            },
            states: {
                scheduleLoading: {
                    id: "scheduleLoading",
                    invoke: {
                        src: "getSchedule",
                        onDone: [
                            {
                                cond: "doesScheduleExist",
                                actions: "assignInitialContext",
                                target: "scheduleLoadingInitial",
                            },
                            {
                                target: "noSchedule",
                            },
                        ],
                        onError: "scheduleLoadingError",
                    },
                },
                noSchedule: {
                    on: {
                        SET_SCHEDULE: {
                            actions: "setSchedule",
                            target: "waitingForScheduleChanges",
                        },
                    },
                },
                waitingForScheduleChanges: {
                    after: {
                        // Allow time for kafka event to create schedule in library service
                        // In future, we will use an event instead that is triggered after NODE_CREATED/SCHEDULE_CREATED is received from web sockets
                        5000: { target: "scheduleLoading" },
                    },
                },
                scheduleLoadingError: {
                    type: "final",
                },
                scheduleLoadingInitial: {
                    id: "scheduleLoadingInitial",
                    invoke: {
                        src: "getScheduleData",
                        onDone: {
                            actions: "setScheduleData",

                            target: "scheduleLoadingScheduleVersions",
                        },
                        onError: "scheduleLoadingInitialError",
                    },
                },
                scheduleLoadingInitialError: {
                    type: "final",
                },
                scheduleLoadingScheduleVersions: {
                    id: "scheduleLoadingScheduleVersions",
                    invoke: {
                        src: "getScheduleVersionData",
                        onDone: [
                            {
                                cond: "isViewByDataField",
                                actions: "setScheduleVersionData",
                                target: "loadViewByDataField",
                            },
                            {
                                cond: "isViewByDayDataField",
                                actions: "setScheduleVersionData",
                                target: "loadViewByDataField",
                            },
                            {
                                actions: "setScheduleVersionData",
                                target: "scheduleLoadingEventBlock",
                            },
                        ],
                        onError: "scheduleLoadingScheduleVersionsError",
                    },
                },
                scheduleLoadingScheduleVersionsError: {
                    type: "final",
                },
                scheduleLoadingEventBlock: {
                    id: "scheduleLoadingEventBlock",
                    invoke: {
                        src: "getEventBlockData",
                        onDone: {
                            actions: "setEventBlockData",
                            target: "idle",
                        },
                        onError: "scheduleLoadingEventBlockError",
                    },
                },
                scheduleLoadingEventBlockError: {
                    type: "final",
                },
                deleteScheduleVersion: {
                    id: "deleteScheduleVersion",
                    invoke: {
                        src: "deleteScheduleVersion",
                        onDone: {
                            actions: "deleteLocalScheduleVersion",
                            target: "idle",
                        },
                    },
                },
                loadViewByDataField: {
                    id: "loadViewByDataField",
                    invoke: {
                        src: "getDataFieldListItems",
                        onDone: {
                            actions: "setDataFieldListItemData",
                            target: "scheduleLoadingEventBlock",
                        },
                        onError: "scheduleLoadingEventBlockError",
                    },
                },
                idle: {
                    id: "idle",
                    on: {
                        REFRESH_SCHEDULE: {
                            target: "scheduleLoading",
                        },
                        GET_EVENT_BLOCK_HIERARCHY: "scheduleLoadingEventBlock",
                        SET_SELECTED_SUB_SCHEDULES: {
                            actions: "setSelectedSubSchedules",
                        },
                        SELECT_ROOT_SCHEDULE_VERSION: {
                            actions: "selectRootScheduleVersion",
                        },
                        ADD_SCHEDULE_VERSIONS: {
                            actions: "addScheduleVersions",
                        },
                        UPDATE_SCHEDULE_VERSION_VISIBILITY: {
                            actions: "updateScheduleVersionVisibility",
                        },
                        UPDATE_VIEW_BY: {
                            actions: "updateViewBy",
                        },
                        UPDATE_SELECTED_VENUE_LOCATIONS: [
                            /*
                                SINGULAR VENUE SELECTION
                            */
                            {
                                cond: "isViewByVenue",
                                actions: "updateSelectedVenueLocationInUrl",
                            },
                            {
                                cond: "isViewByVenueRunSheet",
                                actions: "updateSelectedVenueLocationInUrl",
                            },
                            /*
                                MULTIPLE VENUE SELECTION
                            */
                            {
                                // We bypass the URL update since we don't support multiple ids and update context directly
                                actions: "updateSelectedVenueLocationsMultiple",
                                target: "scheduleLoadingEventBlock",
                            },
                        ],
                        UPDATE_DAY: {
                            actions: "updateDay",
                        },
                        UPDATE_SELECTED_DATA_FIELD_LIST_ITEMS: [
                            /*
                                SINGULAR DATA FIELD SELECTION
                            */
                            {
                                cond: "isViewByDataField",
                                actions: "updateSelectedDataFieldListItemInUrl",
                            },
                            /*
                                MULTIPLE DATA FIELD SELECTION
                            */
                            {
                                actions: "updateSelectedDataFieldListItemsMultiple",
                                target: "scheduleLoadingEventBlock",
                            },
                        ],
                        UPDATE_SCHEDULE_VERSION_NAME: {
                            actions: "updateScheduleVersionName",
                        },
                        UPDATE_SCHEDULE_VERSION_EDIT_NAME: {
                            actions: "updateScheduleVersionEditName",
                        },
                        UPDATE_SCHEDULE_VERSION: [
                            {
                                cond: "isTopLevel",
                                actions: ["updateScheduleNodeFromVersion", "updateScheduleVersion"],
                            },
                            {
                                actions: "updateScheduleNodeFromVersion",
                            },
                        ],
                        DELETE_SCHEDULE_VERSION: {
                            target: "deleteScheduleVersion",
                        },
                        DELETE_SCHEDULE_VERSION_LOCAL: {
                            actions: "deleteLocalScheduleVersion",
                        },
                        UPDATE_EVENT_BLOCK: {
                            actions: "updateEventBlock",
                        },
                        ADD_EVENT_BLOCK: [
                            {
                                cond: "shouldEventBlockShowOnCalendar",
                                actions: "addEventBlock",
                            },
                        ],
                        DELETE_EVENT_BLOCK: {
                            actions: "deleteEventBlock",
                        },
                        WAIT_FOR_SCHEDULE_VERSION_CHILDREN_COPY: {
                            actions: "appendPendingSchedule",
                        },
                        UPDATE_IS_TABLE_VIEW: {
                            actions: "updateIsTableView",
                        },
                        UPDATE_CALENDAR_LEVEL: {
                            actions: "updateCalendarLevel",
                        },
                    },
                },
            },
        },
        {
            services: {
                scheduleRootServices: () => (send) => {
                    const wsSub = scheduleWebSocketSub(send);
                    return () => {
                        wsSub.unsubscribe();
                    };
                },
                getSchedule,
                getScheduleData: (context) => getScheduleData(context.schedule.id),
                getScheduleVersionData: async (context, event) =>
                    getScheduleVersionData(
                        (event.type === "FROM_URL_SELECT_ROOT_SCHEDULE_VERSION" ? event.payload : undefined) ??
                            context.selectedScheduleVersionRootId ??
                            context.availableSchedules.children[0].item.id
                    ),
                getEventBlockData: async (context) => {
                    const promises = [];
                    promises.push(
                        getHierarchyEventBlocks(context.selectedScheduleVersionRootId, context.selectedViewByOption, {
                            day: context.availableDays.get(context.day),
                            venues: context.selectedVenueLocations,
                            dataFieldSelection: context.selectedDataFieldListItems,
                        })
                    );
                    if (context.selectedViewByOption.filterType === FilterType.VenueRunSheet) {
                        promises.push(
                            getEventBlocksDone({
                                day: context.availableDays.get(context.day),
                                venueLocationId: context.selectedVenueLocations[0].item.id,
                                scheduleVersionId: context.selectedScheduleVersionRootId,
                            })
                        );
                    }

                    const [eventBlockHierarchy, eventBlocksDone] = await Promise.all(promises);
                    return { eventBlockHierarchy, eventBlocksDone };
                },
                deleteScheduleVersion: async (_, event) => deleteScheduleVersion(event.payload),
                getDataFieldListItems: async (context, event) => {
                    const listItems = await getDataFieldListItems(scheduleApi, getDisciplineDataField(context.schedule.config.dataFields).id, []);
                    let filterType: FilterType;
                    if (event.type === "FROM_URL_UPDATE_VIEW_BY") {
                        filterType = event.payload.filterType;
                    } else {
                        filterType = context.selectedViewByOption.filterType;
                    }
                    return {
                        listItems,
                        filterType,
                    };
                },
            },
            guards: {
                isCopiedScheduleVersionReadyToLoadEventBlocks: (context, event) => {
                    const contextAfterUpdatePending = updatePendingSchedules(context, event);

                    const pendingAfterUpdatePending = contextAfterUpdatePending.pendingSchedules.find((pending) => pending.rootScheduleVersion.id === event.payload.parentId);

                    return (
                        // The open schedule is the one for which children have been copied
                        context.selectedScheduleVersionRootId === event.payload.parentId &&
                        // We are no longer waiting for any more children to come back
                        !pendingAfterUpdatePending.scheduleVersionsPending.length
                    );
                },
                isCreatedScheduleVersionReadyToLoadEventBlocks: (context, event) => {
                    const pending = context.pendingSchedules.find((pending) => pending.rootScheduleVersion.id === event.payload.id);

                    if (!pending) {
                        return false;
                    }

                    return context.selectedScheduleVersionRootId === event.payload.id && !pending.scheduleVersionsPending.length;
                },
                isCreatedNodeNotPending: (context, event) => !context.pendingSchedules.some((pending) => pending.rootScheduleVersion.id === event.payload.id),
                doesScheduleExist: (context, event: DoneInvokeEvent<Schedule>) => typeof event.data?.config === "object",
                // If any schedule version creations are pending, do not go to "scheduleLoadingScheduleVersions" since this starts a network call
                areScheduleVersionCreationsPending: (context, event) => context.pendingSchedules.some(({ rootScheduleVersion }) => rootScheduleVersion.id === event.payload),
                isViewByDataField: (context, event) =>
                    (event.type === "FROM_URL_UPDATE_VIEW_BY" ? event.payload.filterType : context.selectedViewByOption.filterType) === FilterType.DataField,
                isViewByDayDataField: (context, event) =>
                    (event.type === "FROM_URL_UPDATE_VIEW_BY" ? event.payload.filterType : context.selectedViewByOption.filterType) === FilterType.DayDataField,
                isViewByDayVenueOrSummary: (context, event) => event.payload.filterType === FilterType.DayVenue || event.payload.filterType === FilterType.Summary,
                isViewByVenue: (context, event) =>
                    (event.type === "FROM_URL_UPDATE_VIEW_BY" ? event.payload.filterType : context.selectedViewByOption.filterType) === FilterType.Venue,
                isViewByVenueRunSheet: (context, event) =>
                    (event.type === "FROM_URL_UPDATE_VIEW_BY" ? event.payload.filterType : context.selectedViewByOption.filterType) === FilterType.VenueRunSheet,
                isTopLevel,
                shouldEventBlockShowOnCalendar: (context, event) => showEventBlockOnCalendar(context, event.payload.item),
            },
            actions: {
                updateHistoricalNodeEvents: assign((context, event) => ({
                    ...context,
                    historicalNodeCreatedEvents: [...context.historicalNodeCreatedEvents, event.payload],
                })),
                assignInitialContext: assign((_, event) => getScheduleInitialContext(event.data, locationData$.getValue(), history)),
                setSchedule: assign((_, event) => getScheduleInitialContext(event.payload, locationData$.getValue(), history)),
                setScheduleData: assign({
                    scheduleNode: (_, event) => event.data.scheduleNode,
                    venueLocations: (_, event) => event.data.venueLocations,
                    selectedVenueLocations: (context, event) => {
                        const venueIdFromQuery = locationData$.getValue().query.get(ScheduleQueryParam.VenueLocation);
                        const flattenedVenues = flattenVenueLocations(event.data.venueLocations, context?.schedule?.config?.venueLocationTypes);
                        if (venueIdFromQuery == null) {
                            return flattenedVenues;
                        }
                        const venueFromQuery = flattenedVenues.find((v) => v.item.id === venueIdFromQuery);
                        if (venueFromQuery == null) {
                            return flattenedVenues;
                        }
                        return [venueFromQuery];
                    },
                    selectedScheduleVersionRootId: (_, event) => {
                        const scheduleId = getScheduleIdFromUrlPathname(history.location.pathname);
                        if (scheduleId != null) {
                            return scheduleId;
                        }
                        const firstScheduleId = event.data.availableSchedules.children[0].item.id;
                        updateUrlPathnameWithNewScheduleId(history, firstScheduleId);
                        return firstScheduleId;
                    },
                    availableSchedules: (_, event) => event.data.availableSchedules,
                }),
                setScheduleVersionData: assign({
                    scheduleNode: (context: ScheduleMachineContext, event) => {
                        const convertedSelectedSchedule = convertNodesChildNodesToScheduleNodes(event.data.scheduleVersionNode);

                        const scheduleInsideContext = context.scheduleNode.childNodes.find((node) => node.id === convertedSelectedSchedule.id);

                        if (scheduleInsideContext == null) {
                            context.scheduleNode.childNodes.push(convertedSelectedSchedule);
                        } else {
                            const index = context.scheduleNode.childNodes.findIndex((node) => node.id === convertedSelectedSchedule.id);
                            context.scheduleNode.childNodes[index].childNodes = convertedSelectedSchedule.childNodes;
                        }

                        return context.scheduleNode;
                    },
                    selectedSubScheduleIds: () => [],
                    selectedScheduleVersionRootId: (_, event) => event.data.scheduleVersionNode.id,
                    selectedScheduleVersionHierarchy: (_, event) => event.data.scheduleVersionHierarchy,
                }),
                setEventBlockData: assign({
                    eventBlockHierarchy: (_, event) => event.data.eventBlockHierarchy,
                    eventBlocksDone: (_, event) => event.data.eventBlocksDone,
                }),
                deleteLocalScheduleVersion: assign((context, event) =>
                    deleteLocalScheduleVersion(context, event.type === "DELETE_SCHEDULE_VERSION_LOCAL" ? event.payload : event.data, history)
                ),
                setDataFieldListItemData: assign({
                    availableDataFieldListItems: (_, event) => event.data.listItems,
                    selectedDataFieldListItems: (_, event) => {
                        const allListItems = event.data.listItems;
                        if (event.data.filterType === FilterType.DataField) {
                            const { query } = locationData$.getValue();
                            const listItemIdFromUrl = query.get(ScheduleQueryParam.DataField);
                            if (listItemIdFromUrl != null) {
                                const foundListItems = allListItems.filter((l) => l.id === listItemIdFromUrl);
                                if (foundListItems.length > 0) {
                                    // If filtering by a single data field list item and an id is found in the url that matches, return it
                                    return foundListItems;
                                }
                            }
                            // If filtering by single data field and no id is found in the url search params, we want only to select the first item
                            const firstListItem = event.data.listItems[0];
                            // Update url with selection (reason this is not done at the point of selecting view by data field is because we do not have the list items in local state at that point)
                            query.set(ScheduleQueryParam.DataField, firstListItem.id);
                            updateUrlSearchParam(history, query);
                            // Return the first
                            return [firstListItem];
                        }
                        return allListItems;
                    },
                }),
                setSelectedSubSchedules: assign((_, event) => ({
                    selectedSubScheduleIds: event.payload,
                })),
                selectRootScheduleVersion: (_, event) => {
                    updateUrlPathnameWithNewScheduleId(history, typeof event.payload === "string" ? event.payload : event.payload?.id);
                },
                fromUrlSelectRootScheduleVersion: assign((context, event) => {
                    const scheduleVersion = context.availableSchedules.children.find((s) => s.item.id === event.payload);
                    return {
                        selectedScheduleVersionRootId: scheduleVersion.item.id,
                        selectedSubScheduleIds: [],
                        selectedScheduleVersionHierarchy: scheduleVersion,
                    };
                }),
                addScheduleVersions: assign((context, event) => {
                    const { scheduleNode } = context;
                    const { selectedScheduleVersionHierarchy } = context;
                    const { availableSchedules } = context;
                    // For each schedule version to add
                    event.payload.forEach((scheduleVersion) => {
                        if (scheduleVersion.parentId !== null && scheduleVersion.parentId !== EMPTY_GUID) {
                            const parentScheduleVersionHierarchy = findHierarchyEntityById([selectedScheduleVersionHierarchy], scheduleVersion.parentId) as Hierarchy<
                                ScheduleVersion
                            >;
                            const parentScheduleVersionNode = findScheduleNode([scheduleNode], scheduleVersion.parentId);
                            if (parentScheduleVersionHierarchy != null && parentScheduleVersionNode != null) {
                                parentScheduleVersionHierarchy.children.push({ item: scheduleVersion, children: [] });
                                parentScheduleVersionNode.childNodes.push(createScheduleVersionNode(scheduleVersion.id, scheduleVersion.name, scheduleVersion.parentId));
                            } else {
                                if (parentScheduleVersionHierarchy == null) {
                                    // eslint-disable-next-line no-console
                                    console.warn(`Could find parent schedule version when adding schedule version ${scheduleVersion.id} to hierarchy.`);
                                }
                                if (parentScheduleVersionNode == null) {
                                    // eslint-disable-next-line no-console
                                    console.warn(`Could find parent schedule version node when adding schedule version ${scheduleVersion.id} node.`);
                                }
                            }
                        } else {
                            // If no parent, add to the schedule Nodes array as top level child.
                            scheduleNode.childNodes.unshift(createScheduleVersionNode(scheduleVersion.id, scheduleVersion.name, scheduleVersion.parentId));
                            // No need to add to the selected schedule version, as we can assume its not selected.

                            // Add top level item to the schedule versions list
                            const hierarchyItem: Hierarchy<ScheduleVersion> = { item: scheduleVersion, children: [] };
                            availableSchedules.children.unshift(hierarchyItem);
                        }
                    });
                    return {
                        scheduleNode,
                        selectedScheduleVersionHierarchy,
                        availableSchedules,
                    };
                }),
                updateScheduleVersionVisibility: assign({
                    scheduleNode: (context, event) => {
                        const { scheduleNode } = context;
                        const scheduleVersion = findScheduleNode([scheduleNode], event.payload);
                        const newValue = !scheduleVersion.visible;
                        scheduleVersion.visible = newValue;
                        return scheduleNode;
                    },
                }),
                updateViewBy: (context, event) => updateViewByUrlFromMachine(context, event, locationData$, history),
                viewByDataField: assign((context, event) => ({
                    selectedViewByOption: context.availableViewByOptions.find((v) => v.filterType === event.payload.filterType),
                })),
                viewByDayDataField: assign((context, event) => ({
                    selectedViewByOption: context.availableViewByOptions.find((v) => v.filterType === event.payload.filterType),
                })),
                viewByDayVenueOrSummary: assign((context, event) => ({
                    selectedViewByOption: context.availableViewByOptions.find((v) => v.filterType === event.payload.filterType),
                    selectedVenueLocations: flattenVenueLocations(context.venueLocations, context?.schedule?.config?.venueLocationTypes),
                })),
                viewByVenue: assign((context, event) => ({
                    selectedViewByOption: context.availableViewByOptions.find((v) => v.filterType === event.payload.filterType),
                })),
                viewByVenueRunSheet: assign((context, event) => ({
                    selectedViewByOption: context.availableViewByOptions.find((v) => v.filterType === event.payload.filterType),
                    selectedVenueLocations:
                        context.selectedVenueLocations.length === 1
                            ? context.selectedVenueLocations
                            : [includeThirdLevelVenue(context?.schedule?.config?.venueLocationTypes) ? context.venueLocations[0].children[0] : context.venueLocations[0]],
                })),
                /** Updates the URL directly with selected venue */
                updateSelectedVenueLocationInUrl: (_, event) => {
                    const { query } = locationData$.getValue();
                    query.set(ScheduleQueryParam.VenueLocation, event.payload[0]?.item.id);
                    updateUrlSearchParam(history, query);
                },
                /** Updates the machine context from an event triggered by the URL change */
                fromUrlUpdateSelectedVenueLocations: assign({
                    selectedVenueLocations: (context, event) => [findHierarchyEntityById(context.venueLocations, event.payload)],
                }),
                /** Updates the machine context directly (since we don't store multiple venue locations in the URL, we bypass the URL step) */
                updateSelectedVenueLocationsMultiple: assign({
                    selectedVenueLocations: (_, event) => event.payload,
                }),
                updateDay: (context, { payload: dayNumber }) => {
                    const { query } = locationData$.getValue();
                    query.set(ScheduleQueryParam.Day, dayNumber.toString());
                    updateUrlSearchParam(history, query);
                },
                fromUrlUpdateDay: assign((context, { payload: dayNumber }) =>
                    getCalendarRangeByDay(context.availableDays, dayNumber, [context.schedule.dayStartAt, context.schedule.dayEndAt], context.schedule.timezone)
                ),
                updateSelectedDataFieldListItemInUrl: (_, event) => {
                    const { query } = locationData$.getValue();
                    query.set(ScheduleQueryParam.DataField, event.payload[0]?.id);
                    updateUrlSearchParam(history, query);
                },
                fromUrlUpdateSelectedDataFieldListItems: assign({
                    selectedDataFieldListItems: (context, event) => context.availableDataFieldListItems.filter((l) => l.id === event.payload),
                }),
                updateSelectedDataFieldListItemsMultiple: assign({
                    selectedDataFieldListItems: (_, event) => event.payload,
                }),
                updateScheduleVersionName: assign({
                    scheduleNode: (context, event) => {
                        const { scheduleNode } = context;
                        const scheduleVersion = findScheduleNode([scheduleNode], event.payload.id);
                        scheduleVersion.name = event.payload.name;
                        scheduleVersion.editName = false;
                        return context.scheduleNode;
                    },
                }),
                updateScheduleVersionEditName: assign({
                    scheduleNode: (context, event) => {
                        const { scheduleNode } = context;
                        const scheduleVersion = findScheduleNode([scheduleNode], event.payload.id);
                        scheduleVersion.editName = event.payload.value;
                        return context.scheduleNode;
                    },
                }),
                updateScheduleNodeFromVersion: assign({
                    scheduleNode: (context, event) => {
                        if (event.type === "UPDATE_SCHEDULE_VERSION") {
                            const scheduleVersion = event.payload;
                            const { scheduleNode } = context;
                            const scheduleVersionNode = findScheduleNode([scheduleNode], scheduleVersion.id);
                            scheduleVersionNode.name = scheduleVersion.name;
                            return context.scheduleNode;
                        }
                        return context.scheduleNode;
                    },
                }),
                updateScheduleVersion: assign({
                    availableSchedules: (context, event) => {
                        if (event.type === "UPDATE_SCHEDULE_VERSION") {
                            const scheduleVersion = event.payload;
                            const tempSchedules = context.availableSchedules;
                            const index = tempSchedules.children.findIndex((x) => x.item.id === scheduleVersion.id);
                            tempSchedules.children[index].item.status = scheduleVersion.status;
                            tempSchedules.children[index].item.name = scheduleVersion.name;
                            return tempSchedules;
                        }
                        return context.selectedScheduleVersionHierarchy;
                    },
                }),
                updateEventBlock: assign({
                    eventBlockHierarchy: (context, { payload }) => {
                        const topLevelArray = context.eventBlockHierarchy.children;

                        // Remove updated event blocks which have been moved to a different day if in a day-specific view
                        if ([FilterType.DayVenue, FilterType.DayDataField].includes(context.selectedViewByOption.filterType)) {
                            // Using the parent to bypass the day check for children is temp until we fully support creating eent blocks over any number of days (Story 5935).
                            // A more permanent solution would of required a code refactor.
                            // This section of code has been noted in story 5935's description.
                            const removedItems = payload.updated.filter((item) => {
                                const findTopLevelParent = (item: EventBlock): EventBlock => {
                                    if (item.parentId == null) {
                                        return findHierarchyEntityById(topLevelArray, item.id).item;
                                    }
                                    const parent = findHierarchyEntityById(topLevelArray, item.parentId);
                                    if (parent == null || parent.item == null) {
                                        return findHierarchyEntityById(topLevelArray, item.id).item;
                                    }
                                    return findTopLevelParent(parent.item);
                                };
                                const parent = findTopLevelParent(item);
                                const parentUpdated = payload.updated.find((it) => it.id === parent.id);
                                if (parentUpdated != null) {
                                    return parent.startAt.getDay() !== parentUpdated.startAt.getDay();
                                }
                                return false;
                            });

                            removedItems.forEach((item, index) => {
                                payload.updated.slice(index, 1);
                                removeEventBlock(context, { payload: item.id });
                            });
                        }

                        payload.deleted.forEach((entity) => removeEventBlock(context, { payload: entity.id }));
                        payload.created.forEach((entity) => {
                            const parent = findHierarchyEntityById(topLevelArray, entity.parentId);
                            if (parent) {
                                parent.children.push({ item: entity, children: [] });
                            } else {
                                topLevelArray.push({ item: entity, children: [] });
                            }
                        });
                        payload.updated.forEach((entity) => {
                            const existing = findHierarchyEntityById(topLevelArray, entity.id);
                            if (existing.item.parentId !== entity.parentId) {
                                // If the parent id changes, we must move the entity to its new parent
                                // We can assume a top level event block never reaches here as it's parent is always null
                                const currentParent = findHierarchyEntityById(topLevelArray, existing.item.parentId);
                                currentParent.children = currentParent.children.filter((child) => child.item.id !== entity.id);
                                const newParent = findHierarchyEntityById(topLevelArray, entity.parentId);
                                newParent.children.push({ item: entity, children: existing.children });
                            } else {
                                existing.item = entity;
                            }
                        });

                        return context.eventBlockHierarchy;
                    },
                }),
                addEventBlock: assign({
                    eventBlockHierarchy: (context, event) => {
                        const topLevelArray = context.eventBlockHierarchy.children;
                        const parent = findHierarchyEntityById(topLevelArray, event.payload.item.parentId) as Hierarchy<EventBlock>;
                        if (!parent) {
                            // If parent == null, we know it's a top level event
                            topLevelArray.push(event.payload);
                        } else {
                            parent.children.push(event.payload);
                        }
                        return context.eventBlockHierarchy;
                    },
                }),
                deleteEventBlock: assign((context, event) => {
                    removeEventBlock(context, event);
                    return { eventBlockHierarchy: context.eventBlockHierarchy };
                }),
                appendPendingSchedule: assign((context, event) => ({
                    pendingSchedules: [...context.pendingSchedules, event.payload],
                })),
                updatePendingSchedules: assign(updatePendingSchedules),
                updatePendingNodes: assign((context, event) => {
                    if (event.type === "SCHEDULE_VERSION_NODE_CREATED") {
                        const node = event.payload;
                        const pending = context.pendingSchedules.find((pending) => pending.rootScheduleVersion.id === node.id);

                        if (pending) {
                            pending.rootNodeExists = true;
                        }
                    }

                    return context;
                }),
                addressCompletedPendingSchedules: assign((context, event) => {
                    const payload = (event as ScheduleVersionChildCopied | ScheduleVersionNodeCreated).payload;

                    const scheduleId =
                        (payload as ScheduleVersionChildCopied["payload"]).scheduleId !== undefined ? payload.parentId : (payload as ScheduleVersionNodeCreated["payload"]).id;

                    const completedSchedule = context.pendingSchedules.find(
                        (schedule) => schedule.rootScheduleVersion.id === scheduleId && schedule.scheduleVersionsPending.length === 0 && schedule.rootNodeExists
                    );

                    if (completedSchedule) {
                        // Callback stored in context, don't follow this pattern unless there is no other choice
                        context.onScheduleCopyCompleted(completedSchedule.rootScheduleVersion);
                        return {
                            pendingSchedules: [...context.pendingSchedules.filter((schedule) => schedule !== completedSchedule)],
                        };
                    }

                    return {};
                }),
                updateIsTableView: (_, event) => {
                    const { query } = locationData$.getValue();
                    query.set(ScheduleQueryParam.Table, boolToString(event.payload));
                    updateUrlSearchParam(history, query);
                },
                fromUrlUpdateIsTableView: assign({ isTableView: (_, event) => event.payload }),
                updateCalendarLevel: (_, event) => {
                    const { query } = locationData$.getValue();
                    query.set(ScheduleQueryParam.CalendarLevel, String(event.payload));
                    updateUrlSearchParam(history, query);
                },
                fromUrlUpdateCalendarLevel: assign({
                    calendarLevel: (_, event) => event.payload,
                }),
            },
        }
    );

function updatePendingSchedules(context: ScheduleMachineContext, event: ScheduleVersionChildCopied): Partial<ScheduleMachineContext> {
    const child = event.payload;
    const waitingForSchedule = context.pendingSchedules.find((schedule) => schedule.rootScheduleVersion.id === child.parentId);

    if (!waitingForSchedule) {
        // This schedule is not one we're interested in since we're not waiting for it
        return {};
    }

    const waitingForWithoutSchedule = context.pendingSchedules.filter((wfs) => wfs !== waitingForSchedule);

    const removedPending = waitingForSchedule.scheduleVersionsPending.find(({ name }) => name === child.name);

    const remainingWaitingFor: SchedulesWaitingFor = {
        ...waitingForSchedule,
        scheduleVersionsPending: waitingForSchedule.scheduleVersionsPending.filter((pending) => pending !== removedPending),
    };

    const rootLevelSchedule = context.scheduleNode.childNodes.find((schedule) => schedule.id === child.parentId);

    // Clones the selectedScheduleVersionHierarchy to prevent mutating the original
    const selectedScheduleVersionHierarchy = JSON.parse(JSON.stringify(context.selectedScheduleVersionHierarchy));

    if (context.selectedScheduleVersionHierarchy.item.id === child.parentId) {
        selectedScheduleVersionHierarchy.children.push({ item: child, children: [] });
    }

    return {
        pendingSchedules: [...waitingForWithoutSchedule, remainingWaitingFor],
        selectedScheduleVersionHierarchy,
        scheduleNode: {
            ...context.scheduleNode,
            childNodes: [
                ...context.scheduleNode.childNodes.filter((node) => node !== rootLevelSchedule),
                { ...rootLevelSchedule, childNodes: [...rootLevelSchedule.childNodes, createScheduleVersionNode(child.id, child.name, child.parentId)] },
            ],
        },
    };
}
